import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';

/**
 * PaymentMethodPicker — donor-facing payment-method chooser for the PUBLIC
 * campaign page. Rendered only when the campaign opts in
 * (campaign.showPaymentMethodPicker); otherwise the page keeps its historical
 * "straight to the Tranzila iframe" flow untouched.
 *
 * Order (per design): bit → credit card → Apple Pay → Google Pay.
 *
 * Method visibility:
 *   - Credit card — always shown (opens the existing iframe flow via onSelectCredit).
 *   - bit         — enableBit && bitAvailable (org has GamaPay) && currency === 'ILS'.
 *   - Apple Pay   — enableApplePay && ApplePaySession.canMakePayments(). That feature
 *                   check already encodes the device rules: Mac+Safari ✓, Mac+other ✗,
 *                   iPhone (Safari/Chrome) ✓, Windows/Android ✗.
 *   - Google Pay  — enableGooglePay && (Windows || Android) only — never on Apple devices.
 *
 * Apple/Google charge natively through Tranzila Hosted Fields. The thtk +
 * charge params come from GET /public/wallet/charge-params (passed in as
 * requestWalletCharge); those params carry notify_url + order_id so a
 * successful wallet charge creates a real Donation via the same
 * /payments/tranzila/callback the iframe uses.
 */

interface WalletChargeResponse {
  ok: boolean;
  thtk?: string;
  terminalName?: string;
  chargeParams?: Record<string, string>;
  note?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  campaignName?: string;
  enableBit: boolean;
  enableGooglePay: boolean;
  enableApplePay: boolean;
  bitAvailable: boolean;
  onSelectCredit: () => void;
  onSelectBit: () => void;
  requestWalletCharge: (method: 'google' | 'apple') => Promise<WalletChargeResponse>;
  onWalletSuccess: (payload: Record<string, string>) => void;
  onWalletError: (msg: string) => void;
}

// ── Brand logos (match the admin debug page) ────────────────────────────────
const GoogleG = () => (
  <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const AppleLogo = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff" aria-hidden>
    <path d="M17.05 12.04c-.03-2.77 2.26-4.1 2.36-4.16-1.29-1.88-3.29-2.14-4-2.17-1.7-.17-3.32 1-4.18 1-.86 0-2.19-.98-3.6-.95-1.85.03-3.56 1.08-4.51 2.73-1.93 3.35-.49 8.31 1.38 11.03.92 1.33 2.01 2.82 3.44 2.77 1.38-.06 1.9-.89 3.57-.89 1.66 0 2.13.89 3.59.86 1.48-.03 2.42-1.36 3.33-2.7 1.05-1.55 1.48-3.05 1.5-3.13-.03-.01-2.88-1.1-2.91-4.37zM14.28 4.13c.76-.92 1.27-2.2 1.13-3.47-1.09.04-2.42.73-3.2 1.65-.7.81-1.31 2.11-1.15 3.36 1.22.09 2.46-.62 3.22-1.54z" />
  </svg>
);
const CardLogo = () => (
  <svg viewBox="0 0 40 28" width="30" height="21" aria-hidden>
    <rect x="0.5" y="0.5" width="39" height="27" rx="4" fill="url(#pmpcardg)" />
    <rect x="0" y="6" width="40" height="5" fill="#1f2937" opacity="0.55" />
    <circle cx="24" cy="20" r="6" fill="#EB001B" opacity="0.9" />
    <circle cx="31" cy="20" r="6" fill="#F79E1B" opacity="0.85" />
    <defs>
      <linearGradient id="pmpcardg" x1="0" y1="0" x2="40" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#6366F1" /><stop offset="1" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
  </svg>
);

const loadScriptCache: Record<string, Promise<void> | undefined> = {};
function loadScript(src: string): Promise<void> {
  const ex = loadScriptCache[src];
  if (ex) return ex;
  const p = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = () => resolve(); s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
  loadScriptCache[src] = p;
  return p;
}

// ── Device detection (matches the agreed rules) ─────────────────────────────
function detectDevice() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isWindows = /Windows/.test(ua);
  let appleCapable = false;
  try { appleCapable = !!(window as any).ApplePaySession && (window as any).ApplePaySession.canMakePayments(); } catch { appleCapable = false; }
  return { isIOS, isAndroid, isWindows, appleCapable };
}

const PaymentMethodPicker: React.FC<Props> = ({
  open, onClose, amount, currency, campaignName,
  enableBit, enableGooglePay, enableApplePay, bitAvailable,
  onSelectCredit, onSelectBit, requestWalletCharge, onWalletSuccess, onWalletError,
}) => {
  const fieldsRef = useRef<any>(null);
  const sdkReadyRef = useRef(false);
  const googleReadyRef = useRef(false);
  // Pre-fetched handshake (thtk + charge params). MUST be ready before the tap:
  // chargeGpay/hfChargeApple open the wallet sheet, which the browser only
  // allows synchronously inside the user's tap gesture. Awaiting the network
  // call inside the tap handler loses that gesture and the sheet never opens
  // (callback never fires → stuck spinner). So we fetch on open and charge
  // synchronously on tap.
  const walletChargeRef = useRef<WalletChargeResponse | null>(null);
  const chargeTimerRef = useRef<any>(null);
  const [busy, setBusy] = useState<null | 'google' | 'apple'>(null);
  const [show, setShow] = useState({ apple: false, google: false });
  // SDK readiness per wallet. chargeGpay/hfChargeApple only work AFTER the SDK
  // signals the wallet is available (Google: the 'googleIsEnable' event; Apple:
  // fields 'ready' + ApplePaySession). Tapping before that no-ops → stuck
  // spinner, so the buttons stay disabled until ready (per Tranzila docs).
  const [ready, setReady] = useState({ apple: false, google: false });

  const showBit = enableBit && bitAvailable && currency === 'ILS';

  // TEMP diagnostic — POST to the server so mobile tests (no console) are
  // visible in CloudWatch. Remove once the wallet flow is confirmed.
  const diag = (data: any) => {
    try { fetch('/api/public/wallet/diag', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data), keepalive: true }); } catch { /* ignore */ }
  };

  // Decide wallet visibility from the donor's device once the dialog opens.
  // Do NOT reset the SDK refs on close — the SDK instance (fieldsRef) persists
  // across opens, so googleIsEnable / 'ready' only fire on the FIRST init. On
  // reopen we re-seed `ready` from the refs, otherwise the button would stay
  // disabled (the "can't tap Google Pay a second time" bug).
  useEffect(() => {
    if (!open) return;
    const d = detectDevice();
    setShow({
      apple: enableApplePay && d.appleCapable,
      google: enableGooglePay && (d.isWindows || d.isAndroid),
    });
    setReady({
      google: googleReadyRef.current,
      apple: sdkReadyRef.current && typeof (window as any).hfChargeApple === 'function' && !!(window as any).ApplePaySession,
    });
  }, [open, enableApplePay, enableGooglePay]);

  // Pre-load + initialise the Hosted Fields SDK while the donor reads the
  // dialog, so a wallet tap charges without an init delay. Off-screen card
  // fields are required by the SDK even though wallets don't use them.
  useEffect(() => {
    if (!open) return;
    if (!show.apple && !show.google) return;
    let cancelled = false;
    (async () => {
      try {
        await loadScript('https://hf.tranzila.com/assets/js/thostedf.js');
        if (show.apple) await loadScript('https://hf.tranzila.com/assets/js/thfapplepay.js');
        if (cancelled) return;
        const Tzla = (window as any).TzlaHostedFields;
        if (!Tzla?.create || fieldsRef.current) return;
        const fields = Tzla.create({
          sandbox: false,
          fields: {
            credit_card_number: { selector: '#pmp_cc', placeholder: '4580 4580 4580 4580', tabindex: 1 },
            cvv: { selector: '#pmp_cvv', placeholder: '123', tabindex: 2 },
            expiry: { selector: '#pmp_exp', placeholder: '12/26', version: '1' },
          },
          styles: {
            input: { height: 'auto', width: 'auto' },
            select: { height: 'auto', width: 'auto' },
          },
        });
        fieldsRef.current = fields;
        // Apple Pay readiness depends only on ApplePaySession + the SDK 'ready'
        // event (per Tranzila docs); enable the button once both hold.
        fields.onEvent?.('ready', () => {
          sdkReadyRef.current = true;
          diag({ at: 'sdk-ready' });
          if (show.apple && typeof (window as any).hfChargeApple === 'function' && (window as any).ApplePaySession) {
            setReady(r => ({ ...r, apple: true }));
          }
        });
        // Per the docs, HF detects Google Pay internally and fires googleIsEnable
        // — "no further action is required to detect the wallet." We do NOT load
        // Google's pay.js or create our own PaymentsClient: doing so conflicts
        // with the SDK's internal Google client. Enable the button only on this
        // event, exactly as the docs show.
        fields.onEvent?.('googleIsEnable', () => {
          googleReadyRef.current = true;
          diag({ at: 'googleIsEnable' });
          setReady(r => ({ ...r, google: true }));
        });
      } catch (e: any) { diag({ at: 'sdk-init-failed', error: e?.message || String(e) }); }
    })();
    return () => { cancelled = true; };
  }, [open, show.apple, show.google]);

  // Pre-fetch the thtk + charge params on open (one handshake serves both
  // wallets — it's per terminal+amount, not per method) so the tap handler can
  // charge synchronously and keep the user gesture.
  useEffect(() => {
    if (!open) { walletChargeRef.current = null; return; }
    if (!show.apple && !show.google) return;
    let cancelled = false;
    walletChargeRef.current = null;
    requestWalletCharge('google')
      .then(res => { if (!cancelled) { walletChargeRef.current = res; diag({ at: 'prefetch', ok: res?.ok, terminal: res?.chargeParams?.terminal_name, note: res?.note }); } })
      .catch(e => { if (!cancelled) diag({ at: 'prefetch-failed', error: e?.message || String(e) }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, show.apple, show.google]);

  useEffect(() => () => { if (chargeTimerRef.current) clearTimeout(chargeTimerRef.current); }, []);

  if (!open) return null;

  // Tranzila Hosted Fields callback: (err, response). `err.messages[]` carries
  // VALIDATION failures; a processor DECLINE comes back as a `response` with
  // transaction_response.success === false (processor_response_code !== "000"),
  // NOT as err — so we must inspect transaction_response, not just `err`.
  const chargeCb = (method: 'google' | 'apple') => (err: any, response: any) => {
    if (chargeTimerRef.current) { clearTimeout(chargeTimerRef.current); chargeTimerRef.current = null; }
    setBusy(null);
    diag({ at: 'callback', method, err: err ?? null, response: response ?? null });
    try { console.log('[wallet]', method, 'err=', JSON.stringify(err), 'response=', JSON.stringify(response)); } catch { console.log('[wallet]', method, err, response); }
    if (err) {
      const msg = (err && (err.messages?.[0]?.message || err.message)) || '';
      // User-cancelled wallet sheets are not real errors — just reopen the picker.
      if (/cancel/i.test(String(msg) + JSON.stringify(err || {}))) return;
      onWalletError(msg || 'התשלום לא הושלם');
      return;
    }
    const tr = response?.transaction_response;
    const approved = tr?.success === true || String(tr?.processor_response_code) === '000';
    if (approved) {
      // The Donation is created server-side via notify_url; this just drives the
      // redirect to the thank-you page.
      onWalletSuccess({
        method,
        ...(tr?.transaction_id ? { transactionId: String(tr.transaction_id) } : {}),
      });
    } else {
      onWalletError(
        tr?.error
        || (tr?.processor_response_code ? `התשלום נדחה (קוד ${tr.processor_response_code})` : 'התשלום לא הושלם')
      );
    }
  };

  // Synchronous — runs inside the tap gesture so the wallet sheet is allowed to
  // open. Uses the pre-fetched charge params + thtk (REQUIRED per the docs); NO
  // await before opening the sheet.
  const payWallet = (method: 'google' | 'apple') => {
    if (busy) return;
    if (!ready[method]) { diag({ at: 'not-ready', method }); return; }
    const res = walletChargeRef.current;
    if (!res || !res.ok || !res.thtk || !res.chargeParams) {
      diag({ at: 'not-prepped', method, ok: res?.ok, hasThtk: !!res?.thtk, note: res?.note });
      onWalletError('אמצעי התשלום עדיין נטען — נסו שוב בעוד רגע, או בכרטיס אשראי.');
      return;
    }
    const params = { ...res.chargeParams, thtk: res.thtk };
    diag({ at: 'charging', method, terminal: res.chargeParams?.terminal_name, thtk: !!res.thtk, amount: res.chargeParams?.amount, sdkReady: sdkReadyRef.current, googleReady: googleReadyRef.current, hasGpay: typeof fieldsRef.current?.chargeGpay === 'function', hasApple: typeof (window as any).hfChargeApple === 'function' });
    // Safety: if the SDK never invokes the callback (e.g. the sheet is blocked),
    // don't leave the spinner stuck forever.
    if (chargeTimerRef.current) clearTimeout(chargeTimerRef.current);
    chargeTimerRef.current = setTimeout(() => {
      chargeTimerRef.current = null;
      setBusy(null);
      diag({ at: 'no-callback-timeout', method });
      onWalletError('לא התקבלה תשובה מאמצעי התשלום. נסו שוב או בכרטיס אשראי.');
    }, 45000);
    try {
      if (method === 'google') {
        const fn = fieldsRef.current?.chargeGpay;
        if (typeof fn !== 'function') { clearTimeout(chargeTimerRef.current); chargeTimerRef.current = null; diag({ at: 'no-gpay-fn', method }); onWalletError('Google Pay אינו זמין'); return; }
        // chargeGpay accepts a NARROWER param set than the iframe. Passing
        // params it doesn't recognise (notify_url_address, pdesc) made it reject
        // the thtk with 10017. Send only the documented Google Pay params, plus
        // notify_url + order_id for the donation callback (matches the working
        // debug flow's minimal call).
        const cp = res.chargeParams;
        // EXACTLY the 6 params the working debug page sends — nothing else.
        // Extra params made chargeGpay reject the thtk (10017) or hang. Once this
        // confirms the charge succeeds, notify_url/order_id are added back for
        // the donation linkage.
        const gp: Record<string, string> = {
          terminal_name: cp.terminal_name,
          response_language: cp.response_language,
          currency_code: cp.currency_code,
          amount: cp.amount,
          tran_mode: cp.tran_mode,
          thtk: res.thtk,
        };
        setBusy(method);
        fn.call(fieldsRef.current, gp, chargeCb('google'));
      } else {
        const fn = (window as any).hfChargeApple;
        if (typeof fn !== 'function') { clearTimeout(chargeTimerRef.current); chargeTimerRef.current = null; diag({ at: 'no-apple-fn', method }); onWalletError('Apple Pay אינו זמין'); return; }
        // labelApple shows on the Apple Pay sheet — use the campaign name.
        // Tranzila requires it non-empty with no trailing punctuation.
        const labelApple = (campaignName || '').trim().replace(/[.,;:!?־–-]+$/, '').slice(0, 64) || 'תרומה';
        setBusy(method);
        fn({ ...params, labelApple, supported_network: ['visa', 'masterCard', 'amex', 'discover'] }, chargeCb('apple'));
      }
    } catch (e: any) {
      if (chargeTimerRef.current) { clearTimeout(chargeTimerRef.current); chargeTimerRef.current = null; }
      setBusy(null);
      diag({ at: 'payWallet-threw', method, error: e?.message || String(e) });
      onWalletError(e?.message || 'שגיאה בתשלום');
    }
  };

  const amountLabel = `₪${Number(amount).toLocaleString('he-IL')}`;

  // Shared style for every method button so they line up uniformly: same width,
  // height, radius, and centered content. Only the background differs per brand.
  const methodBtn = (bg: string): React.CSSProperties => ({
    width: '100%', height: 56, background: bg, border: 'none', borderRadius: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    cursor: 'pointer', marginBottom: 10,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} dir="rtl">
      {/* Card-field containers the SDK injects iframes into (wallets don't use
          them). Positioned off-screen but given a REAL size — zero-height /
          overflow:hidden made the injected iframes 0px, which can break the
          SDK's internal init that the Google Pay flow relies on. */}
      <div style={{ position: 'absolute', left: -9999, top: 0, width: 300, opacity: 0, pointerEvents: 'none' }} aria-hidden>
        <div id="pmp_cc" style={{ width: 300, height: 30 }} />
        <div id="pmp_cvv" style={{ width: 300, height: 30 }} />
        <div id="pmp_exp" style={{ width: 300, height: 30 }} />
      </div>

      <div style={{ width: 360, maxWidth: '100%', background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f1f1f1' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>בחירת אמצעי תשלום</div>
          <button onClick={onClose} aria-label="סגור" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}><X size={20} /></button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#111' }}>{amountLabel}</div>
            {campaignName && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{campaignName}</div>}
          </div>

          {/* All methods share one uniform row: full width, same height, centered
              brand glyph + label. */}
          {showBit && (
            <button onClick={() => { onClose(); onSelectBit(); }} style={methodBtn('#16383C')}>
              <span style={{ fontFamily: "'Varela Round', sans-serif", color: '#57DEDF', fontWeight: 700, fontSize: 22, letterSpacing: '-0.5px' }}>bit</span>
            </button>
          )}

          <button onClick={() => { onClose(); onSelectCredit(); }}
            style={{ ...methodBtn('#fff'), border: '1px solid #d1d5db' }}>
            <CardLogo />
            <span style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>כרטיס אשראי</span>
          </button>

          {show.apple && (
            <button onClick={() => payWallet('apple')} disabled={!!busy || !ready.apple}
              style={{ ...methodBtn('#000'), cursor: busy ? 'wait' : (ready.apple ? 'pointer' : 'default'), opacity: (busy && busy !== 'apple') || !ready.apple ? 0.5 : 1 }}>
              {busy === 'apple' || !ready.apple ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <><AppleLogo /><span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>Apple Pay</span></>}
            </button>
          )}
          {show.google && (
            <button onClick={() => payWallet('google')} disabled={!!busy || !ready.google}
              style={{ ...methodBtn('#000'), cursor: busy ? 'wait' : (ready.google ? 'pointer' : 'default'), opacity: (busy && busy !== 'google') || !ready.google ? 0.5 : 1 }}>
              {busy === 'google' || !ready.google ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <><GoogleG /><span style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>Google Pay</span></>}
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 16, color: '#9ca3af', fontSize: 12 }}>
            תשלום מאובטח · Tranzila
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodPicker;
